import React, { useState, useEffect, useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import DocsModalContent from './docs-modal.styled';
import Button from '../button';
import PdfViewer from '../pdf-viewer';

const DocsModal = ({
  isOpened,
  onClose,
  doc,
  docsModalIndex,
  docsCount,
  onPrev,
  onNext,
  onFinish,
}) => {
  const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);

  // Get document URL for stable comparison
  const docUrl = doc?.fullUrl || doc?.url;

  // Reset scroll state when document changes
  useEffect(() => {
    setIsScrolledToBottom(false);
  }, [docUrl, docsModalIndex]);

  // Disable body scroll when modal is open
  useEffect(() => {
    if (isOpened) {
      // Save current scroll position
      const scrollY = window.scrollY;

      // Prevent scrolling
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';

      return () => {
        // Restore scrolling
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.width = '';
        document.body.style.overflow = '';

        // Restore scroll position
        window.scrollTo(0, scrollY);
      };
    }
  }, [isOpened]);

  // Use useCallback to create stable callback reference
  const handleScrollToEnd = useCallback(() => {
    setIsScrolledToBottom(true);
  }, []);

  const canProceed = isScrolledToBottom;

  // Extract token from URL if present
  const getTokenFromUrl = (url) => {
    try {
      const urlObj = new URL(url);
      return urlObj.searchParams.get('token');
    } catch (e) {
      return null;
    }
  };

  // Extract base URL without token
  const getBaseUrl = (url) => {
    try {
      const urlObj = new URL(url);
      urlObj.searchParams.delete('token');
      return urlObj.toString();
    } catch (e) {
      return url;
    }
  };

  if (!isOpened || !doc) return null;

  const token = getTokenFromUrl(doc.fullUrl);
  const baseUrl = getBaseUrl(doc.fullUrl);

  return (
    <div
      className="popup-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(47,46,65,0.9)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
      }}
    >
      <DocsModalContent>
        <div className="docs-modal-header">
          <div className="docs-modal-title">{doc.display_name || doc.name}</div>
          <button
            className="close-button"
            onClick={onClose}
            aria-label="Zavrieť modal"
          >
            <svg width="28" height="28" viewBox="0 0 24 24"><path d="M18.3 5.71a1 1 0 0 0-1.41 0L12 10.59 7.11 5.7A1 1 0 0 0 5.7 7.11l4.89 4.89-4.89 4.89a1 1 0 1 0 1.41 1.41l4.89-4.89 4.89 4.89a1 1 0 0 0 1.41-1.41l-4.89-4.89 4.89-4.89a1 1 0 0 0 0-1.41z" fill="#333"/></svg>
          </button>
        </div>
        <div className="docs-modal-content-area">
          <PdfViewer
            key={`pdf-${docsModalIndex}-${doc.url || doc.fullUrl}`}
            url={baseUrl}
            token={token}
            onScrollToEnd={handleScrollToEnd}
            className="docs-modal-pdf-viewer"
          />
        </div>
        <div className={`docs-modal-read-notice ${canProceed ? 'hidden' : ''}`}>
          <FormattedMessage id="global_read_full_document" />
        </div>
        <div className="docs-modal-actions">
          <Button type="SECONDARY" onClick={onPrev} disabled={docsModalIndex === 0}>Späť</Button>
          {docsModalIndex < docsCount - 1 ? (
            <Button type="PRIMARY" onClick={onNext} disabled={!canProceed}>Ďalej</Button>
          ) : (
            <Button type="PRIMARY" onClick={onFinish} disabled={!canProceed}>Dokončiť</Button>
          )}
        </div>
        <div className="docs-modal-pagination">
          Dokument {docsModalIndex + 1} / {docsCount}
        </div>
      </DocsModalContent>
    </div>
  );
};

export default DocsModal;
